// /pages/BlogDashboardPage.js

import React, { lazy, Suspense, useState, useEffect, useCallback } from 'react';
import axios from '../services/axios';

import Header from '../components/Header';
import PostForm from '../features/posts/PostForm';
import PostList from '../features/posts/PostList';

import { useUser } from '../context/UserContext';
import { useBlog } from '../context/BlogContext';
import { useBlogModal } from '../context/BlogModalContext';
import { useAuth } from '../context/AuthContext';
import { Helmet } from 'react-helmet';
import { startWebSocket, addWebSocketListener, removeWebSocketListener } from '../services/websocketManager';
import styled from 'styled-components';

const MediaModal = lazy(() => import('../components/MediaModal'));
const GOOGLE_MAP_LIBRARIES = ['marker'];

// Styled Containers
const PageWrapper = styled.div`
  padding: 2rem;
  background: ${({ theme }) => theme.background || '#f4f4f4'};
  min-height: 100vh;
`;

const EditorSection = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 2rem;
`;

const FormContainer = styled.div`
  flex: 1;
`;

const Divider = styled.hr`
  margin: 40px 0;
`;

const PostsWrapper = styled.div`
  margin-bottom: 3rem;
`;

const LoadingText = styled.p`
  color: ${({ theme }) => theme.text || '#333'};
  text-align: center;
  margin-top: 2rem;
`;

const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin: 10px 0 20px;
`;

const SortButton = styled.button`
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid rgba(0,0,0,0.15);
  background: white;
  cursor: pointer;
  font-weight: 600;

  &:hover {
    filter: brightness(0.98);
  }
`;

function BlogDashboardPage() {
  const { authChecked, can, isPlatformAdmin } = useAuth();
  const [posts, setPosts] = useState([]);

  // ✅ Story mode default for backfill; later you can switch this to 'desc'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' | 'desc'

  const preloadImageUrl =
    posts.length > 0 &&
    (posts[0].media?.[0]?.webp_url || posts[0].media?.[0]?.media_url);

  const [modalMediaItems, setModalMediaItems] = useState([]);
  const [modalMediaIndex, setModalMediaIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);

  const { user, userloading } = useUser();
  const { blog: selectedBlog, blogloading } = useBlog();
  const { openBlogSelectModal } = useBlogModal();

  const [timezones, setTimezones] = useState([]);
  const [loadingTimezones, setLoadingTimezones] = useState(true);

  const fetchPosts = useCallback(async () => {
    if (!authChecked || !selectedBlog) return;

    setIsLoading(true);
    try {
      const response = await axios.get(`/api/posts/?order=${sortOrder}`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setIsLoading(false);
    }
  }, [authChecked, selectedBlog, sortOrder]);


  useEffect(() => {
    if (!authChecked) return;

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = `${protocol}//${window.location.host}/ws/video-updates/`;

    const handleSocketMessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (
          data?.type === "video_status" &&
          (data.status === "processed" || data.status === "failed")
        ) {
          console.log(
            `🔁 WebSocket: Refreshing posts due to video ${data.status} (post ${data.post_id}, media ${data.media_id})`
          );
          fetchPosts();
        }
      } catch (error) {
        console.error("❌ [WebSocket] Failed to parse message:", error);
      }
    };

    addWebSocketListener(wsUrl, handleSocketMessage);

    return () => {
      removeWebSocketListener(handleSocketMessage);
    };
  }, [authChecked, fetchPosts]);
  
  useEffect(() => {
    if (!isPlatformAdmin && !blogloading && !selectedBlog) {
      openBlogSelectModal(() => {});
    }
  }, [isPlatformAdmin, selectedBlog, blogloading, openBlogSelectModal]);

  useEffect(() => {
    if (authChecked && user && selectedBlog) {
      fetchPosts();
    }
  }, [authChecked, user, selectedBlog, fetchPosts]);

  useEffect(() => {
    async function fetchTimezones() {
      try {
        const response = await axios.get('/api/timezones/');
        setTimezones(response.data.timezones);
      } catch (error) {
        console.error('Error fetching timezones:', error);
      } finally {
        setLoadingTimezones(false);
      }
    }
    fetchTimezones();
  }, []);

  const handleEdit = (post) => {
    setEditingPost(post);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (postId) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      axios
        .delete(`/api/posts/${postId}/`)
        .then(() => fetchPosts())
        .catch((error) => console.error('Error deleting post:', error));
    }
  };

  const handlePostSubmit = async (formData, postId) => {
    try {
      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      let res;
      if (postId) {
        res = await axios.put(`/api/posts/${postId}/`, formData, config);
      } else {
        res = await axios.post('/api/posts/', formData, config);
      }
      setEditingPost(null);
      fetchPosts();
      return res?.data;
    } catch (error) {
      console.error('Error submitting post:', error);
      return null;
    }
  };

  const handleMediaClick = (mediaArray, clickedIndex) => {
    if (clickedIndex !== -1) {
      setModalMediaItems(mediaArray);
      setModalMediaIndex(clickedIndex);
    }
  };

  if (userloading || blogloading || !authChecked) return <LoadingText>Checking authentication...</LoadingText>;
  if (!isPlatformAdmin && !selectedBlog) {
    return <LoadingText>Please select a blog to continue.</LoadingText>;
  }

  return (
    <>
      {preloadImageUrl && (
        <Helmet>
          <link
            rel="preload"
            as="image"
            href={preloadImageUrl}
            imagesrcset={preloadImageUrl}
            type="image/webp"
          />
        </Helmet>
      )}

      <PageWrapper>
        <Header />

        {can('posts:create') && (
          <EditorSection>
            <FormContainer>
              <h2>{editingPost ? 'Edit Post' : 'Create a New Post'}</h2>
              <PostForm
                post={editingPost || {}}
                isEditing={!!editingPost}
                onSave={handlePostSubmit}
                onCancel={() => setEditingPost(null)}
                timezones={timezones}
              />
            </FormContainer>
          </EditorSection>
        )}

        <Divider />

        {/* ✅ Sort toggle */}
        <ToolbarRow>
          <span style={{ opacity: 0.75, fontWeight: 600 }}>Sort:</span>
          <SortButton
            type="button"
            onClick={() => setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
            title="Toggle post order"
          >
            {sortOrder === 'asc' ? 'Oldest → Newest' : 'Newest → Oldest'}
          </SortButton>
        </ToolbarRow>

        <PostsWrapper>
          {isLoading ? (
            <LoadingText>Loading posts...</LoadingText>
          ) : posts.length === 0 ? (
            <LoadingText>No posts found.</LoadingText>
          ) : (
            <PostList
              posts={posts}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onImageClick={handleMediaClick}
            />
          )}
        </PostsWrapper>

        {modalMediaItems.length > 0 && (
          <Suspense fallback={null}>
            <MediaModal
              mediaItems={modalMediaItems}
              currentIndex={modalMediaIndex}
              onClose={() => {
                setModalMediaItems([]);
                setModalMediaIndex(0);
              }}
            />
          </Suspense>
        )}
      </PageWrapper>
    </>
  );
}

export default BlogDashboardPage;
